import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Pencil, X } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import AgentButton from "../AgentButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NEWS_CATS = ["centre", "climat", "transition", "innovation", "environnement", "appels", "conferences", "partenariats", "evenements", "vie", "veille"];
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("ghp-admin-token")}` });
const inputCls = "w-full border border-graphite/20 bg-white px-4 py-3 text-sm text-graphite focus:outline-none focus:border-forest transition-colors duration-200";
const labelCls = "overline-mono mb-2 block text-graphite-soft";
const EMPTY = { title_fr: "", title_en: "", summary_fr: "", summary_en: "", content_fr: "", content_en: "", category: "vie" };

const NewsTab = () => {
  const { t, lang } = useLanguage();
  const [news, setNews] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/news`, { headers: authHeader() });
      setNews(data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("ghp-admin-token");
        window.location.href = "/admin/login";
      }
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB", { day: "numeric", month: "long", year: "numeric" });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/news/${editingId}`, form, { headers: authHeader() });
        toast.success(t("admin.newsUpdated"));
      } else {
        await axios.post(`${API}/admin/news`, form, { headers: authHeader() });
        toast.success(t("admin.newsSaved"));
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch {
      toast.error(t("forms.error"));
    }
    setSaving(false);
  };

  const startEdit = (n) => {
    setForm({
      title_fr: n.title_fr,
      title_en: n.title_en || "",
      summary_fr: n.summary_fr || "",
      summary_en: n.summary_en || "",
      content_fr: n.content_fr,
      content_en: n.content_en || "",
      category: n.category,
    });
    setEditingId(n.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    if (!window.confirm(t("admin.confirmDelete"))) return;
    try {
      await axios.delete(`${API}/admin/news/${id}`, { headers: authHeader() });
      toast.success(t("admin.deleted"));
      load();
    } catch {
      toast.error(t("forms.error"));
    }
  };

  return (
    <div data-testid="admin-news-tab">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-6 border border-science/25 bg-science-soft/40 p-6 md:p-8" data-testid="admin-agent-panel">
        <div className="max-w-2xl">
          <p className="overline-mono text-science">{t("newsAgent.title")}</p>
          <p className="mt-2 text-sm leading-relaxed text-graphite-soft">{t("newsAgent.desc")}</p>
        </div>
        <AgentButton onDone={load} />
      </div>
      <div className="grid gap-10 lg:grid-cols-5">
        <form onSubmit={submit} className="space-y-5 border border-graphite/10 bg-white p-6 lg:col-span-2" data-testid="admin-news-form">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-bold tracking-tight text-graphite">
              {editingId ? t("admin.newsEdit") : t("admin.newsNew")}
            </h2>
            {editingId && (
              <button
                type="button"
                onClick={() => { setForm(EMPTY); setEditingId(null); }}
                className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-graphite-soft hover:text-destructive transition-colors"
                data-testid="admin-news-cancel-edit"
              >
                <X className="h-3.5 w-3.5" />
                {t("admin.cancel")}
              </button>
            )}
          </div>
          <div>
            <label className={labelCls}>{t("admin.titleFr")}</label>
            <input required className={inputCls} value={form.title_fr} onChange={(e) => setForm({ ...form, title_fr: e.target.value })} data-testid="admin-news-title-fr" />
          </div>
          <div>
            <label className={labelCls}>{t("admin.titleEn")}</label>
            <input className={inputCls} value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} data-testid="admin-news-title-en" />
          </div>
          <div>
            <label className={labelCls}>{t("admin.category")}</label>
            <select className={inputCls} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="admin-news-category">
              {NEWS_CATS.map((c) => (
                <option key={c} value={c}>{t(`newsCats.${c}`)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>{t("admin.summaryFr")}</label>
            <textarea rows={2} className={inputCls} value={form.summary_fr} onChange={(e) => setForm({ ...form, summary_fr: e.target.value })} data-testid="admin-news-summary-fr" />
          </div>
          <div>
            <label className={labelCls}>{t("admin.summaryEn")}</label>
            <textarea rows={2} className={inputCls} value={form.summary_en} onChange={(e) => setForm({ ...form, summary_en: e.target.value })} data-testid="admin-news-summary-en" />
          </div>
          <div>
            <label className={labelCls}>{t("admin.contentFr")}</label>
            <textarea rows={5} required minLength={10} className={inputCls} value={form.content_fr} onChange={(e) => setForm({ ...form, content_fr: e.target.value })} data-testid="admin-news-content-fr" />
          </div>
          <div>
            <label className={labelCls}>{t("admin.contentEn")}</label>
            <textarea rows={5} className={inputCls} value={form.content_en} onChange={(e) => setForm({ ...form, content_en: e.target.value })} data-testid="admin-news-content-en" />
          </div>
          <button
            type="submit"
            disabled={saving}
            className="w-full bg-forest px-6 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors hover:bg-graphite disabled:opacity-50"
            data-testid="admin-news-submit"
          >
            {saving ? t("forms.sending") : editingId ? t("admin.update") : t("admin.publish")}
          </button>
        </form>
        <div className="space-y-4 lg:col-span-3">
          {news.length === 0 && (
            <p className="border border-dashed border-graphite/20 py-16 text-center text-sm text-graphite-soft">{t("admin.empty")}</p>
          )}
          {news.map((n) => (
            <article key={n.id} className="flex items-start justify-between gap-4 border border-graphite/10 bg-white p-6" data-testid={`admin-news-${n.id}`}>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-forest">
                  {t(`newsCats.${n.category}`)} — {fmtDate(n.created_at)}
                  {n.agent_generated && (
                    <span className="ml-3 inline-block border border-science/30 bg-science-soft/60 px-2 py-0.5 text-science">
                      {t("newsAgent.badge")}
                    </span>
                  )}
                </p>
                <h3 className="mt-2 font-display text-lg font-bold tracking-tight text-graphite">{n.title_fr}</h3>
                {n.summary_fr && <p className="mt-1 text-sm text-graphite-soft">{n.summary_fr}</p>}
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <button
                  onClick={() => startEdit(n)}
                  aria-label={t("admin.newsEdit")}
                  className="text-graphite/30 transition-colors hover:text-forest"
                  data-testid={`admin-news-edit-${n.id}`}
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  onClick={() => del(n.id)}
                  aria-label={t("admin.delete")}
                  className="text-graphite/30 transition-colors hover:text-destructive"
                  data-testid={`admin-news-delete-${n.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
};

export default NewsTab;
